import React, { useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './components/LoginPage';
import { MockLoginPage } from './components/MockLoginPage';
import { SupabaseLoginPage } from './components/auth/SupabaseLoginPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Dashboard } from './components/dashboards/Dashboard';
import { Calculator } from './components/Calculator';
import CallbackHandler from './components/auth/CallbackHandler';
import { Button } from './components/ui/Button';
import { TooltipProvider } from './components/ui/Tooltip';
import { AppLayoutShell } from './components/layout/AppLayoutShell';
import { useAuth } from './hooks/useAuth';
import { BillingAccessGate } from './components/billing/BillingAccessGate';
import { CookieConsent } from './components/common/CookieConsent';
import { ScrollToTop } from './components/ScrollToTop';
import { NotFoundPage } from './pages/error/NotFoundPage';

// Lazy-loaded components for better initial bundle size
const UnifiedAdminPanel = lazy(() => import('./components/UnifiedAdminPanel'));
const MonitoringDashboard = lazy(() => import('./components/monitoring/MonitoringDashboard'));
const CompanyAdminDashboard = lazy(() => import('./components/dashboards/CompanyAdminDashboard').then(m => ({ default: m.CompanyAdminDashboard })));
const QuotesPage = lazy(() => import('./pages/QuotesPage').then(m => ({ default: m.QuotesPage })));
const QuoteDetailPage = lazy(() => import('./pages/QuoteDetailPage').then(m => ({ default: m.QuoteDetailPage })));
const NewQuotePage = lazy(() => import('./pages/NewQuotePage').then(m => ({ default: m.NewQuotePage })));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage').then(m => ({ default: m.UserProfilePage })));
const OrgMembersPage = lazy(() => import('./pages/OrgMembersPage').then(m => ({ default: m.OrgMembersPage })));
const OrgInvitePage = lazy(() => import('./pages/OrgInvitePage').then(m => ({ default: m.OrgInvitePage })));
const OrgBillingPage = lazy(() => import('./pages/OrgBillingPage').then(m => ({ default: m.OrgBillingPage })));
const OrgSettingsPage = lazy(() => import('./pages/OrgSettingsPage').then(m => ({ default: m.OrgSettingsPage })));
const InviteAcceptPage = lazy(() => import('./pages/InviteAcceptPage'));
const InvitationAcceptance = lazy(() => import('./components/auth/InvitationAcceptance').then(m => ({ default: m.InvitationAcceptance })));
const ConfirmEmailPage = lazy(() => import('./components/auth/ConfirmEmailPage'));
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const SupabaseSignupPage = lazy(() => import('./components/auth/SupabaseSignupPage').then(m => ({ default: m.SupabaseSignupPage })));
// Legal Pages
const TermsOfServicePage = lazy(() => import('./pages/legal/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const CookiePolicyPage = lazy(() => import('./pages/legal/CookiePolicyPage').then(m => ({ default: m.CookiePolicyPage })));
// Support & Marketing Pages
const ContactPage = lazy(() => import('./pages/support/ContactPage').then(m => ({ default: m.ContactPage })));
const AboutPage = lazy(() => import('./pages/marketing/AboutPage').then(m => ({ default: m.AboutPage })));
// China Pricing
const ChinaQuotePage = lazy(() => import('./pages/ChinaQuotePage').then(m => ({ default: m.ChinaQuotePage })));
const ChinaAdminPanel = lazy(() => import('./components/ChinaAdminPanel').then(m => ({ default: m.ChinaAdminPanel })));

// Loading fallback component for Suspense
const PageLoadingFallback = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

// Invite Only component for signup redirection
function InviteOnly() {
  React.useEffect(() => {
    window.location.replace('/login');
  }, []);
  return (
    <div className="p-6 text-center">
      Access by invite only. Please check your email for an invitation.
    </div>
  );
}

// Layout wrapper for authenticated routes
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Listen to sidebar actions
  React.useEffect(() => {
    const handleSidebarAction = (event: CustomEvent) => {
      const { action } = event.detail;

      switch (action) {
        case 'admin-panel':
          setShowAdminPanel(!showAdminPanel);
          break;
        // user-profile action removed - now handled by navigation
        default:
          break;
      }
    };

    window.addEventListener('sidebar-action', handleSidebarAction as EventListener);
    return () => window.removeEventListener('sidebar-action', handleSidebarAction as EventListener);
  }, [showAdminPanel]);

  return (
    <AppLayoutShell>
      <BillingAccessGate />
      <div className="flex-1 min-w-0 bg-background">
        <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 lg:pl-8">
          {showAdminPanel ? (
            <ProtectedRoute requiredRole="superadmin" requireOrganization>
              <div className="mb-6 sm:mb-8">
                <UnifiedAdminPanel />

                <Button
                  variant="outline"
                  onClick={() => setShowAdminPanel(false)}
                  className="mt-4"
                >
                  Back to Application
                </Button>
              </div>
            </ProtectedRoute>
          ) : (
            children
          )}
        </main>
      </div>

      {/* Super Admin Login Modal removed — use Supabase only */}
    </AppLayoutShell>
  );
}

// Theme initialization function
const initializeTheme = () => {
  const savedTheme = localStorage.getItem('theme');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

  if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

// App Router Component
function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const [hasInitialLoad, setHasInitialLoad] = React.useState(false);

  // Preserve current route on app load for authenticated users
  React.useEffect(() => {
    if (!hasInitialLoad && !isLoading) {
      setHasInitialLoad(true);

      // Store the current route if user is on a valid app route
      const currentPath = window.location.pathname;
      const isValidAppRoute = [
        '/calculator', '/dashboard', '/quotes', '/profile', '/org/', '/company-dashboard'
      ].some(route => currentPath.startsWith(route));

      if (isAuthenticated && isValidAppRoute && currentPath !== '/') {
        // Intentionally no-op so authenticated users remain on their current route.
      }
    }
  }, [isAuthenticated, isLoading, hasInitialLoad]);

  // Initialize theme on app load
  React.useEffect(() => {
    initializeTheme();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      {/* Cookie Consent Banner - Shows on all pages */}
      <CookieConsent />

      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* Public routes - always accessible */}
          {/* /landing route removed - redirects to https://estimation.pk */}

          {/* Legal Pages - Public (lazy-loaded) */}
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />

          {/* Support Pages - Public (lazy-loaded) */}
          <Route path="/contact" element={<ContactPage />} />

          {/* Marketing Pages - Public (lazy-loaded) */}
          <Route path="/about" element={<AboutPage />} />

          {/* Supabase callback handling */}
          <Route path="/callback" element={<CallbackHandler />} />
          {/* Email confirmation landing (lazy-loaded) */}
          <Route path="/confirm" element={<ConfirmEmailPage />} />
          {/* Invite acceptance routes (public, lazy-loaded) */}
          <Route path="/invitation/:id" element={<InvitationAcceptance />} />
          <Route path="/invite/org/:orgId" element={<InviteAcceptPage />} />

          {/* Authentication routes */}
          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : (
              import.meta.env.VITE_ENABLE_MOCK_AUTH === 'true' ? <MockLoginPage /> :
                (import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY ? <SupabaseLoginPage /> : <LoginPage />)
            )
          } />
          <Route path="/signup" element={<InviteOnly />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Root route - decide between landing and dashboard */}
          <Route path="/" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } />

          {/* Protected routes - require authentication */}
          {isAuthenticated ? (
            <Route path="/*" element={
              <AuthenticatedLayout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/calculator" element={<Calculator />} />
                  <Route path="/china" element={<ChinaQuotePage />} />
                  <Route path="/china/admin" element={
                    <ProtectedRoute requiredRole="superadmin">
                      <ChinaAdminPanel />
                    </ProtectedRoute>
                  } />
                  <Route path="/new-quote" element={
                    <ProtectedRoute requiredRoles={['admin', 'company_admin']}>
                      <NewQuotePage />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/company-dashboard" element={
                    <ProtectedRoute requiredRole="admin" requireOrganization>
                      <CompanyAdminDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/org/members" element={
                    <ProtectedRoute requiredRole="admin" requireOrganization>
                      <OrgMembersPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/org/invite" element={
                    <ProtectedRoute requiredRole="admin" requireOrganization>
                      <OrgInvitePage />
                    </ProtectedRoute>
                  } />
                  <Route path="/org/billing" element={
                    <ProtectedRoute requiredRole="admin" requireOrganization excludeUsers={['talha.sajid@salsoft.net']}>
                      <OrgBillingPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/org/settings" element={
                    <ProtectedRoute requiredRole="admin" requireOrganization>
                      <OrgSettingsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/quotes" element={<QuotesPage />} />
                  <Route path="/quotes/:id" element={<QuoteDetailPage />} />
                  <Route path="/monitoring" element={
                    <ProtectedRoute requiredRole="superadmin" excludeUsers={['talha.sajid@salsoft.net']}>
                      <MonitoringDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin" element={
                    <ProtectedRoute requiredRole="admin" requireOrganization>
                      <UnifiedAdminPanel />
                    </ProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <UserProfilePage />
                    </ProtectedRoute>
                  } />
                </Routes>
              </AuthenticatedLayout>
            } />
          ) : (
            // Redirect all other routes to login when not authenticated
            <Route path="*" element={<Navigate to="/login" replace />} />
          )}

          {/* 404 Catch-all - Must be last */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </TooltipProvider>
  );
}

function App() {
  return (
    <>
      <ScrollToTop />
      <AppContent />
    </>
  );
}

export default App;
